import type {
  ResetPasswordResultApi,
  UpdateUserRolePayload,
  UpdateUserRoleResponse,
  UserApiResponse,
} from '@/domains/user/user.entity';
import type { UserRepository } from '@/domains/user/user.repository';
import { useAuthStore } from '@/shared/stores';
import { userApiService } from '../http/user.api';

/**
 * Repositorio de gestión de usuarios que maneja operaciones CRUD y administración de cuentas
 * Cumple con ISP - Interface Segregation Principle
 */
class UserApiRepository implements UserRepository {
  /**
   * Obtiene lista de usuarios excluyendo al usuario actualmente autenticado
   */
  async getUsers(): Promise<UserApiResponse> {
    const usersResponse = await userApiService.getUsers();

    if (!usersResponse) throw new Error('Error al obtener los usuarios');

    const currentUserId = useAuthStore.getState().id;

    if (currentUserId)
      usersResponse.data = usersResponse.data.filter(
        (user) => Number.parseInt(user.cod_funcionario, 10) !== currentUserId,
      );

    return usersResponse;
  }

  /**
   * Actualiza el rol de un usuario en el sistema
   * @param payload - Código de funcionario y nuevo rol a asignar
   */
  async updateUserRole(
    payload: UpdateUserRolePayload,
  ): Promise<UpdateUserRoleResponse> {
    const updateResponse = await userApiService.updateUserRole(payload);

    if (!updateResponse)
      throw new Error('Error al actualizar el rol del usuario');

    return updateResponse;
  }

  /**
   * Restablece la contraseña de un usuario a su valor por defecto
   * @param codFuncionario - Identificador único del funcionario
   */
  async resetPasswordUser(
    codFuncionario: number,
  ): Promise<ResetPasswordResultApi> {
    const resetPasswordResponse =
      await userApiService.resetPasswordUser(codFuncionario);

    if (!resetPasswordResponse)
      throw new Error('Error al restablecer la contraseña del funcionario');

    return resetPasswordResponse;
  }
}

export const userApiRepository = new UserApiRepository();
